import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CallKind, CallParticipant } from "@homework/shared";
import { api } from "./api";
import { useAuth } from "./auth";
import { connectToSfu, type SfuSession } from "./sfu";
import { SpeakingDetector } from "./speaking";
import { useRealtime, useSocketEvent } from "./realtime";
import { useToast } from "./toast";

/**
 * Call control contract used by the rest of the UI (presence cards, the office
 * map, the shell's call overlay). The WebRTC implementation lives behind this
 * interface so no other component has to know about peer connections.
 */

export interface ActiveCall {
  callId: string;
  kind: "DIRECT" | "ROOM";
  roomId: string | null;
  participants: CallParticipant[];
  audioOnly: boolean;
  startedAt: string;
}

export interface IncomingCall {
  callId: string;
  from: CallParticipant;
  kind: "DIRECT" | "ROOM";
  audioOnly: boolean;
}

export interface CallControls {
  activeCall: ActiveCall | null;
  incoming: IncomingCall | null;
  /** Local preview stream, if the camera/mic were granted. */
  localStream: MediaStream | null;
  /** Remote media keyed by user id. */
  remoteStreams: Map<string, MediaStream>;
  connecting: boolean;
  muted: boolean;
  cameraOn: boolean;
  screenSharing: boolean;
  /** True when the target is in focus mode and a knock was sent instead. */
  startCall: (
    userId: string,
    options?: { audioOnly?: boolean },
  ) => Promise<{ knocked: boolean } | void>;
  joinRoom: (
    roomId: string,
    options?: { audioOnly?: boolean },
  ) => Promise<void>;
  acceptIncoming: (options?: { audioOnly?: boolean }) => Promise<void>;
  rejectIncoming: (reason?: string) => void;
  leaveCall: () => void;
  toggleMute: () => void;
  toggleCamera: () => Promise<void>;
  /** User ids making sound right now, so the UI can show who is talking. */
  speakingUserIds: Set<string>;
  /** User ids sharing a screen, so the room can put it front and centre. */
  sharingUserIds: Set<string>;
  toggleScreenShare: () => Promise<void>;
}

const NOT_READY = "Call support is not initialised";

export const CallContext = createContext<CallControls | null>(null);

/** Fallback used until the WebRTC provider is mounted. */
const inertControls: CallControls = {
  activeCall: null,
  incoming: null,
  localStream: null,
  remoteStreams: new Map(),
  connecting: false,
  muted: false,
  cameraOn: true,
  screenSharing: false,
  startCall: async () => {
    throw new Error(NOT_READY);
  },
  joinRoom: async () => {
    throw new Error(NOT_READY);
  },
  acceptIncoming: async () => {
    throw new Error(NOT_READY);
  },
  rejectIncoming: () => undefined,
  leaveCall: () => undefined,
  toggleMute: () => undefined,
  toggleCamera: async () => undefined,
  speakingUserIds: new Set<string>(),
  sharingUserIds: new Set<string>(),
  toggleScreenShare: async () => undefined,
};

/** One remote person's audio, playing for as long as the call lasts. */
function RemoteAudio({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.srcObject = stream;
    // Autoplay is usually fine by now — you clicked something to place or
    // accept the call — and a rejection here is not worth interrupting for.
    void element.play().catch(() => undefined);
    return () => {
      element.srcObject = null;
    };
  }, [stream]);

  return <audio ref={ref} autoPlay playsInline />;
}

export function useCalls(): CallControls {
  return useContext(CallContext) ?? inertControls;
}

// ---------------------------------------------------------------- wire shapes

/** `GET /api/calls/config` — everything needed before a peer connection exists. */
interface CallConfigResponse {
  iceServers: RTCIceServer[];
  sfuAvailable: boolean;
  meshMaxParticipants: number;
}

/** `GET /api/calls/room/:roomId/token`. */
interface RoomTokenResponse {
  roomId: string;
  callId: string | null;
  transport: CallTransport;
  token: string | null;
  serverUrl: string | null;
  meshMaxParticipants: number;
  occupants: number;
  capacity: number | null;
}

type CallTransport = "P2P" | "SFU";

/**
 * `call:joinRoom` acks carry the transport and (for an SFU) its join token on
 * top of the `{ ok, callId, error }` declared in packages/shared — exactly as
 * the server's call.handler.ts documents. The base type is derived from the
 * shared contract so this stays assignable, and the extra fields are read
 * defensively in case a deployment predates them.
 */
type JoinRoomAck = { ok: boolean; callId?: string; error?: string } & {
  transport?: CallTransport;
  participants?: CallParticipant[];
  token?: string | null;
  serverUrl?: string | null;
  meshMaxParticipants?: number;
};

type InviteAck = {
  ok: boolean;
  callId?: string;
  error?: string;
  needsKnock?: boolean;
};

/**
 * The opaque payload the server relays for us. It never parses this — the shape
 * is a private contract between two browsers, so it is declared here.
 */
type CallSignal =
  | { kind: "description"; description: RTCSessionDescriptionInit }
  | { kind: "candidate"; candidate: RTCIceCandidateInit };

function parseSignal(value: unknown): CallSignal | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<CallSignal> & Record<string, unknown>;
  if (
    candidate.kind === "description" &&
    candidate.description &&
    typeof candidate.description === "object"
  ) {
    return {
      kind: "description",
      description: candidate.description as RTCSessionDescriptionInit,
    };
  }
  if (
    candidate.kind === "candidate" &&
    candidate.candidate &&
    typeof candidate.candidate === "object"
  ) {
    return {
      kind: "candidate",
      candidate: candidate.candidate as RTCIceCandidateInit,
    };
  }
  return null;
}

/** One remote participant's connection, with its perfect-negotiation state. */
interface Peer {
  userId: string;
  pc: RTCPeerConnection;
  /** The polite peer rolls back on glare; decided by comparing user ids. */
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
  pendingCandidates: RTCIceCandidateInit[];
  stream: MediaStream;
}

interface BeginCallInput {
  callId: string;
  kind: CallKind;
  roomId: string | null;
  audioOnly: boolean;
  transport: CallTransport;
  peerIds: string[];
  /** Present only for an SFU room: where to connect and the credential. */
  sfu?: { serverUrl: string; token: string } | null;
  /** Who is already in the room, from the join ack. */
  participants?: CallParticipant[];
}

/** Host-only ICE if the config call fails: still works on a LAN, never crashes. */
const FALLBACK_ICE: RTCConfiguration = { iceServers: [] };
/** Signals that arrive before the call is set up are held, not dropped. */
const MAX_BUFFERED_SIGNALS = 50;

/**
 * The real implementation: 1:1 peer-to-peer calls (FR-6.4, FR-6.5), meeting-room
 * calls over mesh or an SFU (FR-6.7) and screen sharing (FR-6.8).
 *
 * Media never touches our server: the socket only relays opaque SDP/ICE blobs
 * between two browsers (BRD §10.2). Negotiation follows the "perfect negotiation"
 * pattern so two people pressing call at the same moment cannot deadlock.
 */
export function CallProvider({ children }: { children: ReactNode }) {
  const { socket } = useRealtime();
  const { me, status: authStatus } = useAuth();
  const { push } = useToast();

  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(
    new Map(),
  );
  const [connecting, setConnecting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [speakingUserIds, setSpeakingUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [sharingUserIds, setSharingUserIds] = useState<Set<string>>(new Set());

  // Every callback below is stable (`[]` deps) so the office map's render loop,
  // which depends on joinRoom/leaveCall, is not rebuilt on each render.
  const socketRef = useRef(socket);
  socketRef.current = socket;
  const myIdRef = useRef<string | null>(me?.user.id ?? null);
  myIdRef.current = me?.user.id ?? null;
  const pushRef = useRef(push);
  pushRef.current = push;

  /**
   * Measures who is audible. WebRTC does not report it, and a call where you
   * cannot tell who is talking is the complaint everyone has about a
   * conference bridge.
   */
  const speakingRef = useRef<SpeakingDetector | null>(null);
  speakingRef.current ??= new SpeakingDetector((ids) =>
    setSpeakingUserIds(ids),
  );

  const peersRef = useRef<Map<string, Peer>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const cameraTrackRef = useRef<MediaStreamTrack | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const activeCallRef = useRef<ActiveCall | null>(null);
  const incomingRef = useRef<IncomingCall | null>(null);
  const transportRef = useRef<CallTransport>("P2P");
  /** The media-server session for a room call, when one is in use. */
  const sfuRef = useRef<SfuSession | null>(null);
  /** The room a join is in flight for, so two never overlap. */
  const joiningRoomRef = useRef<string | null>(null);
  const mutedRef = useRef(false);
  const cameraOnRef = useRef(true);
  const screenSharingRef = useRef(false);
  const iceConfigRef = useRef<RTCConfiguration | null>(null);
  const iceConfigPromiseRef = useRef<Promise<RTCConfiguration> | null>(null);
  const bufferedSignalsRef = useRef<
    Map<string, Array<{ fromUserId: string; signal: CallSignal }>>
  >(new Map());

  const setActive = useCallback((call: ActiveCall | null) => {
    activeCallRef.current = call;
    setActiveCall(call);
  }, []);

  const publishRemoteStreams = useCallback(() => {
    const next = new Map<string, MediaStream>();
    for (const peer of peersRef.current.values()) {
      if (peer.stream.getTracks().length > 0)
        next.set(peer.userId, peer.stream);
    }
    setRemoteStreams(next);
  }, []);

  // ------------------------------------------------------------------- config

  const loadIceConfig = useCallback(async (): Promise<RTCConfiguration> => {
    if (iceConfigRef.current) return iceConfigRef.current;
    iceConfigPromiseRef.current ??= api
      .get<CallConfigResponse>("/calls/config")
      .then((response) => {
        const config: RTCConfiguration = {
          iceServers: response.iceServers ?? [],
        };
        iceConfigRef.current = config;
        return config;
      })
      .catch(() => {
        // Degrade instead of failing the call outright: host candidates still
        // connect two people on the same network.
        iceConfigRef.current = FALLBACK_ICE;
        return FALLBACK_ICE;
      })
      .finally(() => {
        iceConfigPromiseRef.current = null;
      });
    return iceConfigPromiseRef.current;
  }, []);

  // -------------------------------------------------------------- local media

  const applyLocalStream = useCallback((stream: MediaStream | null) => {
    localStreamRef.current = stream;
    cameraTrackRef.current = stream?.getVideoTracks()[0] ?? null;
    mutedRef.current = false;
    cameraOnRef.current = Boolean(cameraTrackRef.current);
    setLocalStream(stream);
    setMuted(false);
    setCameraOn(Boolean(cameraTrackRef.current));
  }, []);

  /**
   * FR-6.5 — a refused camera must not sink the call. A denied permission (or a
   * machine with no camera) falls back to audio only and says so.
   */
  const ensureLocalStream = useCallback(
    async (audioOnly: boolean): Promise<MediaStream | null> => {
      if (localStreamRef.current) return localStreamRef.current;
      if (!navigator.mediaDevices?.getUserMedia) {
        pushRef.current({
          kind: "error",
          title: "Calls need a secure connection",
          body: "Your browser will only share a microphone or camera over HTTPS or on localhost.",
        });
        return null;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: !audioOnly,
        });
        applyLocalStream(stream);
        return stream;
      } catch (error) {
        const name = error instanceof DOMException ? error.name : "";
        if (!audioOnly) {
          try {
            const audio = await navigator.mediaDevices.getUserMedia({
              audio: true,
              video: false,
            });
            applyLocalStream(audio);
            pushRef.current({
              kind: "info",
              title: "Joining with audio only",
              body:
                name === "NotAllowedError"
                  ? "Camera access was blocked, so your video stays off. Allow it in your browser to turn it on."
                  : "No camera was available, so your video stays off.",
            });
            return audio;
          } catch {
            // Fall through to the microphone failure below.
          }
        }
        pushRef.current({
          kind: "error",
          title: "No microphone access",
          body:
            name === "NotAllowedError"
              ? "HomeWork needs microphone permission to place a call. Allow it in your browser and try again."
              : "No microphone was available, so nobody would hear you.",
        });
        return null;
      }
    },
    [applyLocalStream],
  );

  const stopLocalMedia = useCallback(() => {
    for (const track of localStreamRef.current?.getTracks() ?? []) track.stop();
    for (const track of screenStreamRef.current?.getTracks() ?? [])
      track.stop();
    // The camera track can already be detached from the stream during a share.
    cameraTrackRef.current?.stop();
    localStreamRef.current = null;
    screenStreamRef.current = null;
    cameraTrackRef.current = null;
    setLocalStream(null);
    setScreenSharing(false);
    screenSharingRef.current = false;
    mutedRef.current = false;
    setMuted(false);
    cameraOnRef.current = true;
    setCameraOn(true);
  }, []);

  // ------------------------------------------------------------ peer plumbing

  const sendSignal = useCallback((targetUserId: string, signal: CallSignal) => {
    const call = activeCallRef.current;
    if (!call) return;
    socketRef.current?.emit("call:signal", {
      callId: call.callId,
      targetUserId,
      signal,
    });
  }, []);

  const closePeer = useCallback(
    (userId: string) => {
      const peer = peersRef.current.get(userId);
      if (!peer) return;
      peer.pc.onicecandidate = null;
      peer.pc.ontrack = null;
      peer.pc.onnegotiationneeded = null;
      peer.pc.onconnectionstatechange = null;
      peer.pc.close();
      peersRef.current.delete(userId);
      publishRemoteStreams();
    },
    [publishRemoteStreams],
  );

  const createPeer = useCallback(
    (peerUserId: string, config: RTCConfiguration): Peer => {
      const existing = peersRef.current.get(peerUserId);
      if (existing) return existing;

      const pc = new RTCPeerConnection(config);
      const peer: Peer = {
        userId: peerUserId,
        pc,
        // Deterministic and opposite on both sides: the lower id is polite.
        polite: (myIdRef.current ?? "") < peerUserId,
        makingOffer: false,
        ignoreOffer: false,
        pendingCandidates: [],
        stream: new MediaStream(),
      };
      peersRef.current.set(peerUserId, peer);

      const local = localStreamRef.current;
      if (local) {
        for (const track of local.getTracks()) pc.addTrack(track, local);
      }

      pc.ontrack = (event) => {
        peer.stream.addTrack(event.track);
        event.track.addEventListener("ended", () => {
          peer.stream.removeTrack(event.track);
          publishRemoteStreams();
        });
        publishRemoteStreams();
        setConnecting(false);
      };

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        sendSignal(peerUserId, {
          kind: "candidate",
          candidate: {
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
            usernameFragment: event.candidate.usernameFragment,
          },
        });
      };

      pc.onnegotiationneeded = () => {
        void (async () => {
          try {
            peer.makingOffer = true;
            await pc.setLocalDescription();
            const description = pc.localDescription;
            if (!description) return;
            sendSignal(peerUserId, {
              kind: "description",
              description: { type: description.type, sdp: description.sdp },
            });
          } catch {
            // A failed offer is retried by the next negotiation event; there is
            // nothing useful to show the person here.
          } finally {
            peer.makingOffer = false;
          }
        })();
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") setConnecting(false);
        if (pc.connectionState === "failed") {
          // A dropped path is usually recoverable without renegotiating.
          try {
            pc.restartIce();
          } catch {
            closePeer(peerUserId);
          }
        }
      };

      return peer;
    },
    [closePeer, publishRemoteStreams, sendSignal],
  );

  const handleSignal = useCallback(
    async (fromUserId: string, signal: CallSignal) => {
      const config = iceConfigRef.current ?? (await loadIceConfig());
      const peer =
        peersRef.current.get(fromUserId) ?? createPeer(fromUserId, config);
      const { pc } = peer;

      try {
        if (signal.kind === "candidate") {
          // Candidates are useless before a remote description exists.
          if (!pc.remoteDescription) {
            peer.pendingCandidates.push(signal.candidate);
            return;
          }
          await pc.addIceCandidate(signal.candidate);
          return;
        }

        const { description } = signal;
        const collision =
          description.type === "offer" &&
          (peer.makingOffer || pc.signalingState !== "stable");
        peer.ignoreOffer = !peer.polite && collision;
        if (peer.ignoreOffer) return;

        // The polite peer's implicit rollback happens inside setRemoteDescription.
        await pc.setRemoteDescription(description);
        for (const candidate of peer.pendingCandidates.splice(0)) {
          try {
            await pc.addIceCandidate(candidate);
          } catch {
            // A candidate from a rolled-back description is safe to drop.
          }
        }
        if (description.type !== "offer") return;
        await pc.setLocalDescription();
        const answer = pc.localDescription;
        if (!answer) return;
        sendSignal(fromUserId, {
          kind: "description",
          description: { type: answer.type, sdp: answer.sdp },
        });
      } catch {
        // Ignoring a bad blob is safer than tearing down a live call: the peer
        // will renegotiate, and a genuinely dead connection surfaces through
        // onconnectionstatechange.
      }
    },
    [createPeer, loadIceConfig, sendSignal],
  );

  const teardown = useCallback(() => {
    const session = sfuRef.current;
    sfuRef.current = null;
    // Disconnecting releases the microphone and camera it opened, so this has
    // to happen before we go looking for stray local tracks to stop.
    if (session) void session.disconnect().catch(() => undefined);
    for (const userId of [...peersRef.current.keys()]) closePeer(userId);
    peersRef.current.clear();
    bufferedSignalsRef.current.clear();
    stopLocalMedia();
    setRemoteStreams(new Map());
    setSpeakingUserIds(new Set());
    setSharingUserIds(new Set());
    setConnecting(false);
    transportRef.current = "P2P";
    setActive(null);
  }, [closePeer, setActive, stopLocalMedia]);

  const teardownRef = useRef(teardown);
  teardownRef.current = teardown;

  // ------------------------------------------------------------- call startup

  const beginCall = useCallback(
    async (input: BeginCallInput) => {
      const call: ActiveCall = {
        callId: input.callId,
        kind: input.kind,
        roomId: input.roomId,
        // Seeded from the ack. Waiting for the broadcast meant a joiner could
        // be listening to a room the panel still showed as empty.
        participants: input.participants ?? [],
        audioOnly: input.audioOnly,
        startedAt: new Date().toISOString(),
      };
      transportRef.current = input.transport;
      setActive(call);
      setConnecting(true);

      if (input.transport === "SFU" && input.sfu) {
        // The media server owns the devices here: it publishes, it mutes, and
        // it renegotiates. Asking for the microphone ourselves as well would
        // open it twice.
        /*
         * Anything still connected goes first.
         *
         * Two overlapping joins — a jittery walk across a room's edge, a
         * rejoin before the first finished — would orphan the earlier session
         * with the microphone still open, and it is not the one the mute
         * button reaches. Somebody muting themselves would carry on being
         * heard through a connection nothing on screen knows about.
         */
        const stale = sfuRef.current;
        sfuRef.current = null;
        if (stale) await stale.disconnect().catch(() => undefined);

        try {
          sfuRef.current = await connectToSfu(
            {
              serverUrl: input.sfu.serverUrl,
              token: input.sfu.token,
              microphone: true,
              // Rooms are joined camera-off; the button opens it.
              camera: false,
            },
            {
              onLocalStream: (next) => {
                localStreamRef.current = next;
                cameraTrackRef.current = next?.getVideoTracks()[0] ?? null;
                setLocalStream(next);
              },
              onRemoteStreams: (streams) => setRemoteStreams(streams),
              // The media server already knows who is audible, so the browser
              // does not have to measure it.
              onActiveSpeakers: (ids) => setSpeakingUserIds(new Set(ids)),
              onScreenShares: (ids) => setSharingUserIds(new Set(ids)),
              onAudioBlocked: () => {
                pushRef.current({
                  kind: "info",
                  title: "Click to hear the room",
                  body: "Your browser is holding audio until you interact with the page.",
                  action: {
                    label: "Play audio",
                    onClick: () =>
                      void sfuRef.current?.startAudio().catch(() => undefined),
                  },
                });
              },
              onConnected: () => setConnecting(false),
              onClosed: () => {
                if (activeCallRef.current?.callId === input.callId)
                  teardownRef.current();
              },
            },
          );
          mutedRef.current = false;
          setMuted(false);
          cameraOnRef.current = false;
          setCameraOn(false);
          // connect() has resolved, so we are in the room whether or not the
          // Connected event is still on its way.
          setConnecting(false);
        } catch (error) {
          pushRef.current({
            kind: "error",
            title: "Could not join the meeting room",
            body:
              error instanceof Error
                ? error.message
                : "The media server refused the connection.",
          });
          socketRef.current?.emit("call:leave", { callId: input.callId });
          teardownRef.current();
        }
        return;
      }

      const [config, stream] = await Promise.all([
        loadIceConfig(),
        ensureLocalStream(input.audioOnly),
      ]);
      // The person may have hung up while the permission prompt was open.
      if (activeCallRef.current?.callId !== input.callId) return;
      if (!stream && input.transport === "P2P") {
        pushRef.current({
          kind: "error",
          title: "Call not connected",
          body: "Nothing to send without a microphone.",
        });
        socketRef.current?.emit("call:leave", { callId: input.callId });
        teardownRef.current();
        return;
      }

      if (input.transport === "P2P") {
        // A peer built from a signal that beat the permission prompt has no
        // local tracks yet; adding them here renegotiates it into a two-way call.
        if (stream) {
          for (const peer of peersRef.current.values()) {
            const senders = peer.pc.getSenders();
            for (const track of stream.getTracks()) {
              if (!senders.some((sender) => sender.track === track))
                peer.pc.addTrack(track, stream);
            }
          }
        }
        for (const peerId of input.peerIds) createPeer(peerId, config);
      }

      // Anything that arrived while we were asking for the camera.
      const buffered = bufferedSignalsRef.current.get(input.callId) ?? [];
      bufferedSignalsRef.current.delete(input.callId);
      for (const entry of buffered)
        void handleSignal(entry.fromUserId, entry.signal);
    },
    [createPeer, ensureLocalStream, handleSignal, loadIceConfig, setActive],
  );

  const startCall = useCallback(
    async (
      userId: string,
      options?: { audioOnly?: boolean },
    ): Promise<{ knocked: boolean } | void> => {
      const client = socketRef.current;
      if (!client)
        throw new Error("You are offline — reconnect before starting a call.");
      if (activeCallRef.current)
        throw new Error(
          "You are already in a call. Leave it before starting another.",
        );

      const audioOnly = options?.audioOnly ?? false;
      const ack = await new Promise<InviteAck>((resolve) => {
        client.emit(
          "call:invite",
          { targetUserId: userId, audioOnly },
          (result) =>
            resolve(
              result ?? { ok: false, error: "The server did not answer." },
            ),
        );
      });

      // FR-6.1–6.3: focus mode turns a call into a knock. Nothing connects and
      // no call session exists until they accept.
      if (!ack.ok && ack.needsKnock) return { knocked: true };
      if (!ack.ok || !ack.callId)
        throw new Error(ack.error ?? "Could not start that call.");

      await beginCall({
        callId: ack.callId,
        kind: "DIRECT",
        roomId: null,
        audioOnly,
        transport: "P2P",
        peerIds: [userId],
      });
      return { knocked: false };
    },
    [beginCall],
  );

  const leaveCall = useCallback(() => {
    const call = activeCallRef.current;
    if (!call) return;
    socketRef.current?.emit("call:leave", { callId: call.callId });
    if (call.kind === "ROOM" && call.roomId)
      socketRef.current?.emit("call:leaveRoom", { roomId: call.roomId });
    teardownRef.current();
  }, []);

  const joinRoom = useCallback(
    async (roomId: string, options?: { audioOnly?: boolean }) => {
      const client = socketRef.current;
      if (!client)
        throw new Error("You are offline — reconnect before joining the room.");
      if (activeCallRef.current?.roomId === roomId) return;
      // The guard above only holds once the join has finished, and the join
      // takes a round trip: without this, walking along a room's edge starts
      // two of them.
      if (joiningRoomRef.current === roomId) return;
      joiningRoomRef.current = roomId;
      try {
        // Walking from one meeting room into another swaps calls.
        if (activeCallRef.current) leaveCall();

        const audioOnly = options?.audioOnly ?? false;
        const ack = await new Promise<JoinRoomAck>((resolve) => {
          client.emit("call:joinRoom", { roomId, audioOnly }, (result) =>
            resolve(
              (result ?? {
                ok: false,
                error: "The server did not answer.",
              }) as JoinRoomAck,
            ),
          );
        });
        if (!ack.ok || !ack.callId)
          throw new Error(ack.error ?? "Could not join that meeting room.");

        let transport: CallTransport = ack.transport ?? "P2P";
        let sfu: { serverUrl: string; token: string } | null = null;
        if (transport === "SFU") {
          // The ack already carries a token; this read is the documented way to
          // fetch one on its own and keeps the SFU path honest when a deployment
          // predates the widened ack.
          const room = await api
            .get<RoomTokenResponse>(
              `/calls/room/${encodeURIComponent(roomId)}/token`,
            )
            .catch(() => null);
          transport = room?.transport ?? transport;
          const token = ack.token ?? room?.token ?? null;
          const serverUrl = ack.serverUrl ?? room?.serverUrl ?? null;
          if (token && serverUrl) {
            sfu = { serverUrl, token };
          } else {
            /*
             * Do not quietly mesh instead.
             *
             * The transport belongs to the call, not to one browser: everyone
             * else in this room is on the media server, and a browser that
             * meshed would connect to the few others that had also failed over
             * and be inaudible to everyone else — a room where some people can
             * hear you and some cannot, with nothing on screen to say why.
             */
            throw new Error(
              "This meeting room runs through the media server and no credential was issued. Reload the page and try again.",
            );
          }
        }

        await beginCall({
          callId: ack.callId,
          kind: "ROOM",
          roomId,
          audioOnly,
          transport,
          // Mesh peers are created from the `call:participants` roster.
          peerIds: [],
          sfu,
          participants: ack.participants,
        });
      } finally {
        // Cleared however it ended, or walking back into this room later would
        // find a join still marked as in flight and do nothing at all.
        if (joiningRoomRef.current === roomId) joiningRoomRef.current = null;
      }
    },
    [beginCall, leaveCall],
  );

  const acceptIncoming = useCallback(
    async (options?: { audioOnly?: boolean }) => {
      const call = incomingRef.current;
      const client = socketRef.current;
      if (!call || !client) return;
      const audioOnly = options?.audioOnly ?? call.audioOnly;
      incomingRef.current = null;
      setIncoming(null);
      client.emit("call:accept", { callId: call.callId, audioOnly });
      await beginCall({
        callId: call.callId,
        kind: call.kind,
        roomId: null,
        audioOnly,
        transport: "P2P",
        peerIds: [call.from.userId],
      });
    },
    [beginCall],
  );

  const rejectIncoming = useCallback((reason?: string) => {
    const call = incomingRef.current;
    if (!call) return;
    incomingRef.current = null;
    setIncoming(null);
    socketRef.current?.emit("call:reject", {
      callId: call.callId,
      reason: reason ?? "Declined",
    });
  }, []);

  // ------------------------------------------------------------ media toggles

  const toggleMute = useCallback(() => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);

    // Both, always. The media server is asked to mute the publication, and the
    // track itself is disabled — one is the correct way to tell the room, the
    // other is the one that holds even if a publication has been orphaned.
    // Silence is the only acceptable outcome of pressing mute.
    for (const track of localStreamRef.current?.getAudioTracks() ?? [])
      track.enabled = !next;
    const session = sfuRef.current;
    if (session)
      void session.setMicrophoneEnabled(!next).catch(() => undefined);
  }, []);

  /**
   * Tells the server whether we are sending video, because peers draw a
   * placeholder for anyone flagged audio-only. Without this, a camera switched
   * on after joining stays invisible to the rest of the room.
   */
  const announceMedia = useCallback((audioOnly: boolean) => {
    const call = activeCallRef.current;
    if (!call) return;
    socketRef.current?.emit("call:media", { callId: call.callId, audioOnly });
  }, []);

  /** Swaps the outgoing video track on every peer without renegotiating. */
  const replaceOutgoingVideo = useCallback((track: MediaStreamTrack | null) => {
    for (const peer of peersRef.current.values()) {
      const sender = peer.pc
        .getSenders()
        .find((entry) => entry.track?.kind === "video");
      if (sender) {
        void sender.replaceTrack(track).catch(() => undefined);
        continue;
      }
      // Someone who joined audio-only has no video sender yet; adding one fires
      // negotiationneeded and the perfect-negotiation path takes it from there.
      if (track && localStreamRef.current)
        peer.pc.addTrack(track, localStreamRef.current);
    }
  }, []);

  const rebuildLocalStream = useCallback(
    (videoTrack: MediaStreamTrack | null) => {
      const audio = localStreamRef.current?.getAudioTracks() ?? [];
      const next = new MediaStream([
        ...audio,
        ...(videoTrack ? [videoTrack] : []),
      ]);
      localStreamRef.current = next;
      setLocalStream(next);
    },
    [],
  );

  /**
   * Meeting rooms are joined with the camera off, so the first press usually
   * has no camera track to toggle — the device was never opened. Acquiring it
   * here is what keeps "off by default" from meaning "off for good", and it is
   * why the camera light only comes on when somebody asks for it.
   */
  const toggleCamera = useCallback(async () => {
    const session = sfuRef.current;
    if (session) {
      const next = !cameraOnRef.current;
      try {
        await session.setCameraEnabled(next);
        cameraOnRef.current = next;
        setCameraOn(next);
        // The roster drives whether peers draw a placeholder, so it has to
        // learn about the camera whichever transport carries it.
        announceMedia(!next);
      } catch {
        pushRef.current({
          kind: "error",
          title: "Could not turn on your camera",
          body: "HomeWork needs camera permission. Allow it in your browser and try again.",
        });
      }
      return;
    }

    const existing = cameraTrackRef.current;
    if (existing) {
      const next = !cameraOnRef.current;
      existing.enabled = next;
      cameraOnRef.current = next;
      setCameraOn(next);
      announceMedia(!next);
      return;
    }

    if (!activeCallRef.current || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const camera = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      const track = camera.getVideoTracks()[0];
      if (!track) return;
      cameraTrackRef.current = track;
      cameraOnRef.current = true;
      setCameraOn(true);
      // Not while sharing a screen: that is what the peers should still see.
      if (!screenSharingRef.current) replaceOutgoingVideo(track);
      rebuildLocalStream(
        screenSharingRef.current
          ? (screenStreamRef.current?.getVideoTracks()[0] ?? null)
          : track,
      );
      announceMedia(false);
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      pushRef.current({
        kind: "error",
        title: "Could not turn on your camera",
        body:
          name === "NotAllowedError"
            ? "HomeWork needs camera permission. Allow it in your browser and try again."
            : "No camera was available.",
      });
    }
  }, [announceMedia, rebuildLocalStream, replaceOutgoingVideo]);

  const stopScreenShare = useCallback(() => {
    if (!screenSharingRef.current) return;
    for (const track of screenStreamRef.current?.getTracks() ?? [])
      track.stop();
    screenStreamRef.current = null;
    screenSharingRef.current = false;
    setScreenSharing(false);
    const camera = cameraTrackRef.current;
    replaceOutgoingVideo(camera);
    rebuildLocalStream(camera);
    announceMedia(!cameraOnRef.current);
  }, [announceMedia, rebuildLocalStream, replaceOutgoingVideo]);

  const toggleScreenShare = useCallback(async () => {
    const session = sfuRef.current;
    if (session) {
      const next = !screenSharingRef.current;
      try {
        await session.setScreenShareEnabled(next);
        screenSharingRef.current = next;
        setScreenSharing(next);
        // Everyone joins a room camera-off, so without this the room is still
        // told we are sending nothing and hides the screen we just shared.
        announceMedia(!next && !cameraOnRef.current);
      } catch {
        // The person cancelled the picker, or the browser refused.
      }
      return;
    }
    if (screenSharingRef.current) {
      stopScreenShare();
      return;
    }
    if (!activeCallRef.current) return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      pushRef.current({
        kind: "error",
        title: "Screen sharing is unavailable",
        body: "This browser cannot share a screen. Try Chrome, Edge, Safari 13+ or Firefox.",
      });
      return;
    }
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      const track = display.getVideoTracks()[0];
      if (!track) {
        for (const stopping of display.getTracks()) stopping.stop();
        return;
      }
      screenStreamRef.current = display;
      screenSharingRef.current = true;
      setScreenSharing(true);
      announceMedia(false);
      // "Stop sharing" in the browser's own bar must restore the camera too.
      track.addEventListener("ended", () => stopScreenShare());
      replaceOutgoingVideo(track);
      rebuildLocalStream(track);
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      if (name === "NotAllowedError") return; // The person cancelled the picker.
      pushRef.current({
        kind: "error",
        title: "Could not share your screen",
        body:
          error instanceof Error
            ? error.message
            : "Screen capture was refused.",
      });
    }
  }, [rebuildLocalStream, replaceOutgoingVideo, stopScreenShare]);

  // ------------------------------------------------------------ socket events

  useSocketEvent("call:incoming", ({ callId, kind, from, audioOnly }) => {
    if (activeCallRef.current || incomingRef.current) {
      // One call at a time: decline politely rather than ringing over a call.
      socketRef.current?.emit("call:reject", {
        callId,
        reason: "On another call",
      });
      pushRef.current({
        kind: "info",
        title: `${from.name} tried to call`,
        body: "You were already in a call, so they were told you are busy.",
      });
      return;
    }
    const next: IncomingCall = { callId, from, kind, audioOnly };
    incomingRef.current = next;
    setIncoming(next);
  });

  useSocketEvent("call:participants", ({ callId, participants }) => {
    const call = activeCallRef.current;
    if (!call || call.callId !== callId) return;
    setActive({ ...call, participants });

    if (transportRef.current !== "P2P") return;
    const myId = myIdRef.current;
    const others = participants.filter(
      (participant) => participant.userId !== myId,
    );
    for (const userId of [...peersRef.current.keys()]) {
      if (!others.some((participant) => participant.userId === userId))
        closePeer(userId);
    }
    void (async () => {
      const config = iceConfigRef.current ?? (await loadIceConfig());
      if (activeCallRef.current?.callId !== callId) return;
      for (const participant of others) {
        if (!peersRef.current.has(participant.userId))
          createPeer(participant.userId, config);
      }
    })();
  });

  useSocketEvent("call:signal", ({ callId, fromUserId, signal }) => {
    const parsed = parseSignal(signal);
    if (!parsed) return;
    if (activeCallRef.current?.callId === callId) {
      void handleSignal(fromUserId, parsed);
      return;
    }
    // The offer can beat our own setup (or the accept). Hold it briefly.
    const queue = bufferedSignalsRef.current.get(callId) ?? [];
    if (queue.length >= MAX_BUFFERED_SIGNALS) return;
    queue.push({ fromUserId, signal: parsed });
    bufferedSignalsRef.current.set(callId, queue);
  });

  useSocketEvent("call:ended", ({ callId }) => {
    bufferedSignalsRef.current.delete(callId);
    if (incomingRef.current?.callId === callId) {
      incomingRef.current = null;
      setIncoming(null);
    }
    if (activeCallRef.current?.callId !== callId) return;
    const wasRoom = activeCallRef.current.kind === "ROOM";
    teardownRef.current();
    pushRef.current({
      kind: "info",
      title: "Call ended",
      body: wasRoom
        ? "Everyone has left the meeting room."
        : "The other person hung up.",
    });
  });

  useSocketEvent("call:rejected", ({ callId, reason }) => {
    if (activeCallRef.current?.callId !== callId) return;
    const name = activeCallRef.current.participants.find(
      (participant) => participant.userId !== myIdRef.current,
    )?.name;
    teardownRef.current();
    pushRef.current({
      kind: "info",
      title: name ? `${name} declined` : "Call declined",
      body: reason,
    });
  });

  // A dropped socket (or a sign-out) cannot leave the camera light on.
  useEffect(() => {
    if (socket || (!activeCallRef.current && !incomingRef.current)) return;
    incomingRef.current = null;
    setIncoming(null);
    teardownRef.current();
  }, [socket]);

  useEffect(() => {
    return () => {
      const call = activeCallRef.current;
      if (call) socketRef.current?.emit("call:leave", { callId: call.callId });
      teardownRef.current();
    };
  }, []);

  useEffect(() => {
    const detector = speakingRef.current;
    if (!detector) return;
    // On the SFU the server reports active speakers, and remoteStreams carries
    // only video there — measuring it would light nobody up. Mesh calls have
    // the audio locally, so they are measured here.
    const watched =
      transportRef.current === "P2P"
        ? new Map(remoteStreams)
        : new Map<string, MediaStream>();
    // Our own microphone is watched either way: a ring on your own tile is the
    // fastest way to notice you are talking while muted.
    const myId = myIdRef.current;
    if (myId && localStream && !muted && transportRef.current === "P2P")
      watched.set(myId, localStream);
    detector.sync(watched);
  }, [remoteStreams, localStream, muted, activeCall]);

  useEffect(() => () => speakingRef.current?.destroy(), []);

  /*
   * Signing out hangs up.
   *
   * The call provider sits above the router and survives the sign-out, so a
   * meeting kept running behind the login screen — microphone included. The
   * server cuts the media connection too, but a browser that hangs up for
   * itself is the difference between stopping now and stopping when a round
   * trip completes.
   */
  useEffect(() => {
    if (authStatus !== "anonymous") return;
    if (!activeCallRef.current) return;
    teardownRef.current();
  }, [authStatus]);

  const value = useMemo<CallControls>(
    () => ({
      activeCall,
      incoming,
      localStream,
      remoteStreams,
      connecting,
      muted,
      cameraOn,
      screenSharing,
      speakingUserIds,
      sharingUserIds,
      startCall,
      joinRoom,
      acceptIncoming,
      rejectIncoming,
      leaveCall,
      toggleMute,
      toggleCamera,
      toggleScreenShare,
    }),
    [
      activeCall,
      incoming,
      localStream,
      remoteStreams,
      connecting,
      muted,
      cameraOn,
      screenSharing,
      speakingUserIds,
      sharingUserIds,
      startCall,
      joinRoom,
      acceptIncoming,
      rejectIncoming,
      leaveCall,
      toggleMute,
      toggleCamera,
      toggleScreenShare,
    ],
  );

  useEffect(() => {
    const detector = speakingRef.current;
    if (!detector) return;
    const watched = new Map(remoteStreams);
    // Watching our own microphone too, so the speaking ring appears on you as
    // well — it is the fastest way to notice you are talking while muted.
    const myId = myIdRef.current;
    if (myId && localStream && !muted) watched.set(myId, localStream);
    detector.sync(watched);
  }, [remoteStreams, localStream, muted]);

  useEffect(() => {
    const detector = speakingRef.current;
    return () => detector?.destroy();
  }, []);

  return (
    <CallContext.Provider value={value}>
      {children}
      {/*
       * Remote audio lives here, not in the call tiles.
       *
       * A tile is only in the DOM once the roster has arrived, is hidden when
       * the camera is off, and disappears entirely when somebody collapses the
       * panel — so hanging playback off it meant whether you could hear anyone
       * depended on all three. This is mounted for as long as the call is.
       *
       * SFU calls attach their own audio (see lib/sfu.ts) and put only video in
       * this map, so nothing is played twice.
       */}
      <div
        className="pointer-events-none fixed h-px w-px overflow-hidden opacity-0"
        aria-hidden
      >
        {[...remoteStreams.entries()]
          .filter(([, stream]) => stream.getAudioTracks().length > 0)
          .map(([userId, stream]) => (
            <RemoteAudio key={userId} stream={stream} />
          ))}
      </div>
    </CallContext.Provider>
  );
}
